import React, { forwardRef, useState, useEffect } from 'react';
import { useForm, useWatch, useFormState, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Button from 'components/Button';
import UploadButton from 'components/UploadButton/UploadButton.admin';

import { evidenceForm as TEXT } from 'locales/en/dispute';
import { fileSchema } from './EvidenceForm.adyen.schema';
import {
  showErrorMessage,
  showSuccessMessage,
} from 'redux/slices/notification';
import {
  DELETE_ADYEN_DISPUTE_DEFENCE,
  SUBMIT_ADYEN_DISPUTE_EVIDENCE,
  UPLOAD_ADYEN_DISPUTE_DEFENCE,
} from 'services/api';
import { useDispatch } from 'react-redux';
import AdyenUploadedDocument from './AdyenUploadedDocument';
import {
  disputeByIdSelector,
  getDisputeReasons,
  getDisputesById,
} from 'redux/slices/dispute';
import Loader from 'components/Loader';
import { useSelector } from 'react-redux';

const EvidenceFormAdyen = ({ dispute }, ref) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { reasons } = useSelector((state) =>
    disputeByIdSelector(state, dispute.id)
  );

  const generateSchema = () => {
    const documents = [];

    reasons?.defenseReasons?.forEach((reason) => {
      reason.defenseDocumentTypes.forEach((doc) => {
        documents.push(doc.defenseDocumentTypeCode);
      });
    });

    return fileSchema(documents);
  };

  useEffect(() => {
    dispatch(getDisputeReasons(dispute.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { handleSubmit, control, setValue, trigger } = useForm({
    resolver: yupResolver(generateSchema()),
  });

  const { errors, isSubmitting } = useFormState({
    control,
  });

  const documentField = useWatch({
    control,
  });

  const onSubmit = async (data) => {
    const defenseReasons = reasons?.defenseReasons || [];

    setIsLoading(true);

    try {
      const responses = await Promise.all(
        defenseReasons.map((reason) => {
          return SUBMIT_ADYEN_DISPUTE_EVIDENCE(
            dispute.id,
            reason.defenseReasonCode
          );
        })
      );

      await Promise.all(
        responses.map((res) => {
          if (res.disputeServiceResult?.errorMessage) {
            dispatch(showErrorMessage(res.disputeServiceResult?.errorMessage));
            return res;
          }
          return res;
        })
      );

      dispatch(getDisputesById(dispute.id));
      dispatch(getDisputeReasons(dispute.id));
      setIsLoading(false);

      dispatch(showSuccessMessage(TEXT.submitEvidenceSuccess));
    } catch (error) {
      dispatch(showErrorMessage(TEXT.submitEvidenceError));
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    setValue(doc.defenseDocumentTypeCode, null);

    if (doc.available) {
      try {
        setIsLoading(true);
        const response = await DELETE_ADYEN_DISPUTE_DEFENCE(
          dispute.id,
          doc.defenseDocumentTypeCode
        );

        if (response.disputeServiceResult?.errorMessage) {
          dispatch(
            showErrorMessage(response.disputeServiceResult?.errorMessage)
          );
          return;
        }

        dispatch(getDisputesById(dispute.id));
        dispatch(getDisputeReasons(dispute.id));

        setIsLoading(false);
      } catch (error) {
        dispatch(showErrorMessage(TEXT.documentDeleteError));
        setIsLoading(false);
      }
    }
  };

  const handleSelectFile = async (fieldName, _file) => {
    setValue(fieldName, _file, { shouldValidate: true });
    await trigger(fieldName);

    if (errors[fieldName]?.message) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', _file);
      formData.append('document_type_code', fieldName);
      const response = await UPLOAD_ADYEN_DISPUTE_DEFENCE(dispute.id, formData);

      dispatch(getDisputeReasons(dispute.id));
      setIsLoading(false);

      if (response.disputeServiceResult?.errorMessage) {
        dispatch(showErrorMessage(response.disputeServiceResult?.errorMessage));
        setValue(fieldName, null);
        return;
      }
    } catch (error) {
      dispatch(showErrorMessage(TEXT.documentUploadError));
      setValue(fieldName, null);
      setIsLoading(false);
    }
  };

  return (
    <>
      <h2
        className="text-base font-bold text-javelin-gray-dark lg:text-xl"
        ref={ref}
      >
        {TEXT.submit}
      </h2>
      <h3 className="mt-2 text-xs text-javelin-gray-light">{TEXT.maxAdyen}</h3>
      {isLoading && <Loader />}
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="w-full mt-5 mb-8"
      >
        <div className="p-0 card">
          {reasons?.defenseReasons?.map((reason) => (
            <div key={reason.defenseReasonCode}>
              <div className="w-full bg-javelin-background">
                <div className="flex p-12">
                  <div className="flex-1">
                    <label className="text-sm">
                      Defense reason: {reason.defenseReasonCode}
                    </label>
                  </div>
                  <div className="flex flex-col flex-1">
                    {reason.defenseDocumentTypes.map((doc) =>
                      doc.available ||
                        documentField[doc.defenseDocumentTypeCode] ? (
                        <AdyenUploadedDocument
                          key={doc.defenseDocumentTypeCode}
                          doc={doc}
                          onDeleteDocument={() => handleDeleteDocument(doc)}
                        />
                      ) : (
                        <Controller
                          key={doc.defenseDocumentTypeCode}
                          control={control}
                          name={doc.defenseDocumentTypeCode}
                          render={() => (
                            <UploadButton
                              label={`${doc.defenseDocumentTypeCode} upload`}
                              labelClassName={'pr-6 ml-auto'}
                              name={doc.defenseDocumentTypeCode}
                              onSelectFile={(_file) =>
                                handleSelectFile(
                                  doc.defenseDocumentTypeCode,
                                  _file
                                )
                              }
                              onDeleteFile={() => handleDeleteDocument(doc)}
                              error={
                                errors[doc.defenseDocumentTypeCode]?.message
                              }
                              file={documentField[doc.defenseDocumentTypeCode]}
                            />
                          )}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
              <hr className="m-0 horizontal-line" />
            </div>
          ))}

          <div className="flex items-center p-12 bg-white">
            <p className="mr-4 text-xs text-javelin-gray-light">
              {TEXT.submitInfo}
            </p>
            <Button
              className="block w-56 h-12 px-0 ml-auto btn-primary lg:w-48"
              type="submit"
              title={TEXT.submit}
              showSpinner={isSubmitting || isLoading}
              isDisabled={isSubmitting || isLoading}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default forwardRef(EvidenceFormAdyen);
